import React from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import CloseIcon from '@mui/icons-material/Close';

import PrestaGlobals from '../../common/PrestaGlobals';
import {
    InterestDraft, Intent, Slot, Timeline, Measures, PHONE_PREFIX,
    phoneValid, firstName, submitInterest, submitInterestExtra, downscalePhoto,
} from './interestDraft';

interface StepProps {
    draft: InterestDraft;
    updateDraft: (patch: Partial<InterestDraft>) => void;
}

const INTENTS: { value: Intent; title: string; sub: string }[] = [
    { value: 'order', title: 'Így tetszik, kérném', sub: 'Egyeztessük a felmérést és a gyártást' },
    { value: 'modify', title: 'Tetszik, de módosítanék', sub: 'Méret, szín, fiókok, gépek…' },
    { value: 'question', title: 'Kérdésem van', sub: 'Az árról, a határidőről, bármiről' },
];

const SLOTS: { value: Slot; label: string; thanks: string }[] = [
    { value: 'morning', label: 'Délelőtt', thanks: 'Délelőtt hívunk' },
    { value: 'afternoon', label: 'Délután', thanks: 'Délután hívunk' },
    { value: 'evening', label: '18 óra után', thanks: '18 óra után hívunk' },
    { value: 'any', label: 'Mindegy', thanks: 'Hamarosan hívunk' },
];

const TIMELINES: { value: Timeline; label: string }[] = [
    { value: '1m', label: '1 hónapon belül' },
    { value: '1-3m', label: '1–3 hónap' },
    { value: 'later', label: 'Ráér' },
];

const MEASURES: { value: Measures; label: string }[] = [
    { value: 'yes', label: 'Igen' },
    { value: 'rough', label: 'Nagyjából' },
    { value: 'survey', label: 'Felmérést kérek' },
];

const MAX_PHOTOS = 3;

// Suffix of the spoken number: 2120-as, 2121-es, 2125-ös
const ONES = ['', 'es', 'es', 'as', 'es', 'ös', 'os', 'es', 'as', 'es'];
const TENS = ['', 'es', 'as', 'as', 'es', 'es', 'as', 'es', 'as', 'es'];
const huNumSuffix = (n: number): string => {
    if (n % 10) return ONES[n % 10];
    if (n % 100) return TENS[(n % 100) / 10];
    if (n % 1000) return 'as';
    return n % 1000000 ? 'es' : 'os';
};

const scrollSx = { flex: '1 1 auto', overflowY: 'auto', px: 2, pt: 1.5, pb: 2, WebkitOverflowScrolling: 'touch' } as const;
const footerSx = { flex: '0 0 auto', px: 2, pt: 1.5, pb: 2, borderTop: '1px solid #eef0ee', bgcolor: '#fff' } as const;
const titleSx = { fontSize: '1.25rem', fontWeight: 700, lineHeight: 1.3, color: '#1f2a24' } as const;
const labelSx = { fontWeight: 600, fontSize: '0.9rem', mb: 0.75 } as const;
// >= 16px: no iOS zoom on focus
const inputSx = { '& .MuiInputBase-input': { fontSize: 16 } } as const;
const disabledSx = { '&.Mui-disabled': { bgcolor: '#c9d0cb', color: '#fff' } } as const;

const ChipRow = <T extends string>({ options, value, onChange }: {
    options: { value: T; label: string }[];
    value: T | null;
    onChange: (v: T | null) => void;
}) => (
    <Stack direction="row" flexWrap="wrap" gap={1}>
        {options.map((o) => (
            <Chip
                key={o.value}
                label={o.label}
                clickable
                color={value === o.value ? 'primary' : 'default'}
                variant={value === o.value ? 'filled' : 'outlined'}
                onClick={() => onChange(value === o.value ? null : o.value)}
                sx={{ height: 40, fontSize: '0.9rem', px: 0.5, borderRadius: 20 }}
            />
        ))}
    </Stack>
);

// Cart reference if set („a/az „Név” tervből”), else the number
const planLabel = (quoteId: number, reference: string): string => {
    const ref = reference.trim();
    if (!ref) return `a #${quoteId}-${huNumSuffix(quoteId)}`;
    return `${/^[aáeéiíoóöőuúüű]/i.test(ref) ? 'az' : 'a'} „${ref}”`;
};

/** 1. Mi érdekel? — tap advances */
export const StepChoice: React.FC<StepProps & { quoteId: number; reference: string }> = ({ quoteId, reference, draft, updateDraft }) => (
    <Box sx={scrollSx}>
        <Typography component="h2" sx={{ ...titleSx, mb: 2 }}>
            Mi érdekel {planLabel(quoteId, reference)} tervből?
        </Typography>
        <Stack gap={1.25}>
            {INTENTS.map((o) => {
                const selected = draft.intent === o.value;
                return (
                    <ButtonBase
                        key={o.value}
                        onClick={() => updateDraft({ intent: o.value, step: 2 })}
                        sx={{
                            display: 'block', textAlign: 'left', width: '100%', minHeight: 68,
                            px: 2, py: 1.5, borderRadius: 3,
                            border: '1.5px solid', borderColor: selected ? 'primary.main' : '#d5d9d6',
                            bgcolor: selected ? 'rgba(49,76,61,0.06)' : '#fff',
                            transition: 'border-color .15s, background-color .15s',
                            '&:hover': { borderColor: 'primary.main' },
                        }}
                    >
                        <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{o.title}</Typography>
                        <Typography variant="body2" sx={{ color: '#5f6b64', mt: 0.25 }}>{o.sub}</Typography>
                    </ButtonBase>
                );
            })}
        </Stack>
        <Typography variant="body2" sx={{ textAlign: 'center', mt: 2, fontWeight: 600, color: 'primary.main' }}>
            Ingyenes, nem kötelez semmire.
        </Typography>
    </Box>
);

type FieldKey = 'name' | 'phone' | 'town' | 'consent';

/** 2. Hogyan érhetünk el? — sends the lead */
export const StepContact: React.FC<StepProps> = ({ draft, updateDraft }) => {
    const [errors, setErrors] = React.useState<Partial<Record<FieldKey, string>>>({});
    const [sending, setSending] = React.useState(false);
    const [submitError, setSubmitError] = React.useState('');
    const refs: Record<FieldKey, React.RefObject<HTMLDivElement | null>> = {
        name: React.useRef<HTMLDivElement>(null),
        phone: React.useRef<HTMLDivElement>(null),
        town: React.useRef<HTMLDivElement>(null),
        consent: React.useRef<HTMLDivElement>(null),
    };

    const set = (patch: Partial<InterestDraft>, clear?: FieldKey) => {
        updateDraft(patch);
        if (clear && errors[clear]) setErrors((e) => ({ ...e, [clear]: undefined }));
    };

    const validate = (): Partial<Record<FieldKey, string>> => {
        const e: Partial<Record<FieldKey, string>> = {};
        if (draft.name.trim().length < 2) e.name = 'Add meg a neved.';
        if (!phoneValid(draft.phone)) e.phone = 'Ellenőrizd a telefonszámot, pl. +36 30 123 4567.';
        if (draft.town.trim().length < 2) e.town = 'Add meg a települést.';
        if (!draft.consent) e.consent = 'A visszahíváshoz el kell fogadnod a tájékoztatót.';
        return e;
    };

    const submit = async () => {
        const e = validate();
        setErrors(e);
        setSubmitError('');
        const first = (Object.keys(refs) as FieldKey[]).find((k) => e[k]);
        if (first) {
            refs[first].current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            refs[first].current?.querySelector('input')?.focus({ preventScroll: true });
            return;
        }
        setSending(true);
        try {
            const sentPhone = await submitInterest(draft);
            updateDraft({ sent: true, sentPhone, step: 3 });
        } catch (err) {
            setSubmitError((err as Error).message);
        } finally {
            setSending(false);
        }
    };

    const messageLabel = draft.intent === 'modify' ? 'Mit változtatnál?' : draft.intent === 'question' ? 'Mi a kérdésed?' : null;

    return (
        <>
            <Box component="form" noValidate onSubmit={(e) => { e.preventDefault(); submit(); }} sx={scrollSx} id="interest-contact">
                <Typography component="h2" sx={{ ...titleSx, mb: 2 }}>Hogyan érhetünk el?</Typography>
                <Stack gap={2}>
                    <TextField
                        ref={refs.name}
                        label="Neved"
                        placeholder="Vezetéknév Keresztnév"
                        value={draft.name}
                        onChange={(e) => set({ name: e.target.value }, 'name')}
                        error={!!errors.name}
                        helperText={errors.name}
                        autoComplete="name"
                        fullWidth
                        sx={inputSx}
                    />
                    <TextField
                        ref={refs.phone}
                        label="Telefonszám"
                        value={draft.phone}
                        onChange={(e) => set({ phone: e.target.value }, 'phone')}
                        onFocus={() => { if (!draft.phone.trim()) updateDraft({ phone: PHONE_PREFIX }); }}
                        error={!!errors.phone}
                        helperText={errors.phone}
                        type="tel"
                        autoComplete="tel"
                        slotProps={{ htmlInput: { inputMode: 'tel' } }}
                        fullWidth
                        sx={inputSx}
                    />
                    <TextField
                        ref={refs.town}
                        label="Település"
                        placeholder="pl. Budapest"
                        value={draft.town}
                        onChange={(e) => set({ town: e.target.value }, 'town')}
                        error={!!errors.town}
                        helperText={errors.town}
                        autoComplete="address-level2"
                        fullWidth
                        sx={inputSx}
                    />
                    <Box>
                        <Typography sx={labelSx}>Mikor hívhatunk?</Typography>
                        <ChipRow options={SLOTS} value={draft.slot} onChange={(v) => updateDraft({ slot: v ?? 'any' })} />
                    </Box>
                    {messageLabel && (
                        <TextField
                            label={<>{messageLabel} <span style={{ fontWeight: 400 }}>(nem kötelező)</span></>}
                            placeholder={draft.intent === 'modify' ? 'pl. a sziget legyen 20 cm-rel rövidebb' : undefined}
                            value={draft.message}
                            onChange={(e) => updateDraft({ message: e.target.value })}
                            multiline
                            minRows={2}
                            maxRows={6}
                            slotProps={{ htmlInput: { maxLength: 1000 } }}
                            fullWidth
                            sx={inputSx}
                        />
                    )}
                    <Box ref={refs.consent}>
                        <FormControlLabel
                            control={<Checkbox checked={draft.consent} onChange={(e) => set({ consent: e.target.checked }, 'consent')} />}
                            label={
                                <Typography variant="body2">
                                    Elfogadom az{' '}
                                    <Link href={PrestaGlobals.privacyURL} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()}>
                                        adatkezelési tájékoztatót
                                    </Link>
                                </Typography>
                            }
                        />
                        {errors.consent && <FormHelperText error sx={{ mt: 0 }}>{errors.consent}</FormHelperText>}
                    </Box>
                </Stack>
            </Box>
            <Box sx={footerSx}>
                {submitError && <Alert severity="error" sx={{ mb: 1.5 }}>{submitError}</Alert>}
                <Button
                    type="submit"
                    form="interest-contact"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={sending}
                    startIcon={sending ? <CircularProgress size={18} color="inherit" /> : undefined}
                    sx={{ py: 1.5, fontWeight: 700, fontSize: '1rem', textTransform: 'none', borderRadius: 2, ...disabledSx }}
                >
                    Kérem a visszahívást
                </Button>
                <Typography variant="caption" component="p" sx={{ textAlign: 'center', mt: 1 }}>
                    Hétköznap általában 2 órán belül hívunk.
                </Typography>
            </Box>
        </>
    );
};

interface Photo { blob: Blob; name: string; url: string }

/** 3. Köszönjük — optional extras onto the same lead */
export const StepThanks: React.FC<StepProps & {
    onClose: () => void;
    onExtraSent: (timeline: Timeline | null, measures: Measures | null, photos: number) => void;
}> = ({ draft, updateDraft, onClose, onExtraSent }) => {
    const [timeline, setTimeline] = React.useState<Timeline | null>(null);
    const [measures, setMeasures] = React.useState<Measures | null>(null);
    const [photos, setPhotos] = React.useState<Photo[]>([]);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');
    const fileRef = React.useRef<HTMLInputElement>(null);
    const photosRef = React.useRef(photos);
    photosRef.current = photos;

    React.useEffect(() => () => photosRef.current.forEach((p) => URL.revokeObjectURL(p.url)), []);

    const slot = SLOTS.find((s) => s.value === draft.slot) ?? SLOTS[3];

    const addPhotos = async (files: FileList | null) => {
        if (!files?.length) return;
        setBusy(true);
        const room = MAX_PHOTOS - photos.length;
        const picked = Array.from(files).slice(0, room);
        const blobs = await Promise.all(picked.map((f) => downscalePhoto(f)));
        setPhotos((p) => [...p, ...blobs.map((blob, i) => ({ blob, name: picked[i].name, url: URL.createObjectURL(blob) }))]);
        setBusy(false);
        if (fileRef.current) fileRef.current.value = '';
    };

    const removePhoto = (i: number) => {
        URL.revokeObjectURL(photos[i].url);
        setPhotos((p) => p.filter((_, j) => j !== i));
    };

    const send = async () => {
        setBusy(true);
        setError('');
        try {
            await submitInterestExtra(timeline, measures, photos.map(({ blob, name }) => ({ blob, name })));
            onExtraSent(timeline, measures, photos.length);
            updateDraft({ extraSent: true });
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setBusy(false);
        }
    };

    const hasExtra = !!timeline || !!measures || photos.length > 0;

    return (
        <>
            <Box sx={{ ...scrollSx, textAlign: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 56, color: 'primary.main' }} />
                <Typography component="h2" sx={{ ...titleSx, mt: 1 }}>Megkaptuk, {firstName(draft.name)}!</Typography>
                <Typography sx={{ mt: 1, color: '#3a4540' }}>
                    {slot.thanks} ezen a számon: <b style={{ whiteSpace: 'nowrap' }}>{draft.sentPhone}</b>
                </Typography>

                {draft.extraSent ? (
                    <Alert icon={<CheckCircleIcon fontSize="inherit" />} severity="success" sx={{ mt: 3, textAlign: 'left' }}>
                        Köszönjük, továbbítottuk a kollégáknak.
                    </Alert>
                ) : (
                    <Box sx={{ mt: 3, p: 2, borderRadius: 3, bgcolor: '#f4f6f4', textAlign: 'left' }}>
                        <Typography sx={{ fontWeight: 700, mb: 2 }}>Rövidebb lesz a hívás, ha most válaszolsz (30 mp)</Typography>
                        <Typography sx={labelSx}>Mikorra kellene?</Typography>
                        <ChipRow options={TIMELINES} value={timeline} onChange={setTimeline} />
                        <Typography sx={{ ...labelSx, mt: 2 }}>Megvannak a pontos méretek?</Typography>
                        <ChipRow options={MEASURES} value={measures} onChange={setMeasures} />
                        <Typography sx={{ ...labelSx, mt: 2 }}>Fotó a helyiségről</Typography>
                        <Stack direction="row" gap={1} flexWrap="wrap">
                            {photos.map((p, i) => (
                                <Box key={p.url} sx={{ position: 'relative', width: 72, height: 72 }}>
                                    <Box component="img" src={p.url} alt="" sx={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 2 }} />
                                    <IconButton
                                        aria-label="Fotó törlése"
                                        size="small"
                                        onClick={() => removePhoto(i)}
                                        sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', boxShadow: 1, p: 0.25, '&:hover': { bgcolor: '#fff' } }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                            {photos.length < MAX_PHOTOS && (
                                <ButtonBase
                                    onClick={() => fileRef.current?.click()}
                                    disabled={busy}
                                    sx={{
                                        flex: photos.length ? '0 0 72px' : '1 1 100%', height: 72, borderRadius: 2,
                                        border: '1.5px dashed #b9c1bc', color: '#5f6b64', gap: 1, fontSize: '0.9rem',
                                    }}
                                >
                                    <AddPhotoAlternateOutlinedIcon />
                                    {!photos.length && 'Fotó feltöltése'}
                                </ButtonBase>
                            )}
                        </Stack>
                        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => addPhotos(e.target.files)} />
                        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                        <Button
                            variant="contained"
                            fullWidth
                            disabled={!hasExtra || busy}
                            onClick={send}
                            startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}
                            sx={{ mt: 2, py: 1.25, fontWeight: 700, textTransform: 'none', borderRadius: 2, ...disabledSx }}
                        >
                            Elküldöm
                        </Button>
                    </Box>
                )}
            </Box>
            <Box sx={footerSx}>
                <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    onClick={onClose}
                    sx={{ py: 1.25, fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
                >
                    Vissza a tervhez
                </Button>
            </Box>
        </>
    );
};
